import { Component, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { AuthenticationService } from 'src/app/core/modules/auth-core/data/service/authentication.service';
import { Raffle, RaffleDrawType, RaffleStatus } from 'src/app/data/schema/raffle';
import { IRaffleEvent } from 'src/app/data/schema/interfaces';
import { RaffleService } from 'src/app/data/service/raffle.service';
import { BasePage } from 'src/app/core/base-page';
import { PrizeStatus } from '../data/schema/prize';
import { ChoiceSelectValue, ServiceHelper } from '../core/helper/services.helper';
import { TicketBook } from '../data/schema/ticket-book';
import { StripeAccountStatus } from '../data/schema/stripe-models';
import { PrizeValidationHelper } from '../core/helper/prize-validation.helper';

@Component({
    template: ''
})
export abstract class RaffleBasePage extends BasePage implements OnDestroy {

    ticketValid = false;
    protected readonly = false;
    private sub: Subscription;

    constructor(
        protected raffleService: RaffleService,
        authService?: AuthenticationService,
    ) {
        super(authService);

        this.sub = raffleService.raffleUpdated.subscribe(() => {
            this.loadData();
        });
    }

    get raffle() { return this.raffleService.currentRaffle; }
    get hasUnsavedChanges() { return this.raffleService.hasChanges(); }
    get sponsorValid() { return this.raffle.sponsors.length > 0; }
    get bankingValid() { return (this.raffleService.stripeAccountInfo?.status === StripeAccountStatus.Ready); }
    get auctionValid() { return this.raffle.auctionAccessCode != null; }
    get isLocked() { return this.readonly; }

    get detailsValid(): boolean {
        const fields = [
            this.raffle.name,
            this.raffle.saleEnd,
            this.raffle.saleStart,
            this.raffle.accessCode,
            this.raffle.raffleLocation,
        ];
        return this.checkValidity(fields);
    }

    get licenseValid(): boolean {
        if (String.isNullOrWhiteSpace(this.raffle.charitableOrganizationNumber) === false &&
            String.isNullOrWhiteSpace(this.raffle.noLicenseReason) === false) {
            // both the charitable organization number and no license reason are provided so valid
            return true;
        } else if (String.isNullOrWhiteSpace(this.raffle.licenseNumber) === false &&
            String.isNullOrWhiteSpace(this.raffle.licensePath) === false) {
            // both the license number and path are provided so valid
            return true;
        }

        return false;
    }

    get brandingValid(): boolean {
        const fields = [this.raffle.bannerPath];
        return this.checkValidity(fields);
    }


    get prizesValid(): boolean {

        const now = new Date().valueOf();

        // if the raffle status is drawn, then prizes are valid
        if (this.raffle.status === RaffleStatus.Drawn) {
            return true;
        } else if (this.raffle.prizes.length > 0) {
            const invalidPrizes = this.raffle.prizes.some(prize => {
                // if prize is drawn then that prize is valid so ignore dates
                if (prize.status === PrizeStatus.pending) {
                    // prize is pending, lets see if the sales end for it passed and the raffle is active
                    const saleEnd = new Date(prize.salesEnd).valueOf();
                    const drawDate = new Date(prize.drawDate).valueOf();

                    const saleEndAlreadyPassed = (saleEnd <= now && this.raffle.status === RaffleStatus.Active);
                    if (saleEndAlreadyPassed === false) {
                        // sales end hasn't passed, so lets validate the draw date and sale date
                        if (saleEnd > drawDate) {
                            return prize;
                        }
                    }

                    // the prize sales end must always fall within the raffle sales period
                    if (PrizeValidationHelper.validateSalesEnd(prize, this.raffle).valid === false) {
                        return prize;
                    }
                }
            });

            // any invalid prizes means we have issues
            if (invalidPrizes === true) {
                return false;
            } else if (this.raffle.prizes.some(m => m.salesEnd === this.raffle.saleEnd) === false) {
                // if we have no prizes that match the sales end then invalid
                return false;
            }
        } else {
            // no prizes? not setup
            return false;
        }

        return true;
    }

    get organizationValid() {
        const fields = [
            this.raffle.country,
            this.raffle.currency,
            this.raffle.organizationName,
            //this.raffle.organizationWebsite, NOT REQUIRED
            this.raffle.contactEmail,
        ];
        if (['CA', 'US', 'AU'].includes(this.raffle.country)) {
            fields.push(this.raffle.province);
        }
        if (this.raffle.country === 'CA' && this.raffle.province === 'BC') {
            fields.push(this.raffle.contactPhone);
        }
        return this.checkValidity(fields);
    }

    get aboutRaffleValid(): boolean {
        return !String.isNullOrWhiteSpace(this.raffle.description) &&
            !String.isNullOrWhiteSpace(this.raffle.rules) &&
            !String.isNullOrWhiteSpace(this.raffle.prizeClaimInstructions);
    }

    async areTicketsValid() {
        // test total value is set
        const fields = [this.raffle.totalValue];
        if (this.checkValidity(fields) === false) {
            return false;
        }
        // test that we have pricing tiers
        if (this.raffle.pricingTiers.length === 0) {
            return false;
        }
        // test total tier value === totalValue
        const tierTotals = this.raffle.pricingTiers.reduce((n, { quantity, price }) => n + (price * quantity), 0);
        if (tierTotals !== this.raffle.totalValue) {
            return false;
        }
        // if manual we test that packs are set and we have enough
        if (this.raffle.drawType === RaffleDrawType.Manual) {
            const ticketBooks = await TicketBook.fetchForRaffle(this.raffle.raffleId);

            const totalTicketBookQty = ticketBooks.reduce(
                (quantity, ticketBook) => (+ticketBook.qty) ? quantity + +ticketBook.qty : quantity, 0);

            const totalTicketTierQty = this.raffle.pricingTiers.reduce(
                (quantity, ticketTier) => (+ticketTier.quantity) ? quantity + (+ticketTier.quantity * ticketTier.ticketQuantity) : quantity, 0);

            if (totalTicketBookQty < totalTicketTierQty) {
                return false;
            }
        }
        // we got to here so return true
        return true;
    }

    ngOnDestroy() {
        if (this.sub != null) { this.sub.unsubscribe(); }
    }


    exportCSV(data, name) {
        if (data) {
            // const dlEle = document.createElement('a');
            // dlEle.setAttribute('download', `${name}.csv`);
            // dlEle.href = `data:text/csv;charset=utf-8,${data}`;
            // dlEle.click();

            // Create a blob
            // source: https://stackoverflow.com/a/68146412/7015266
            // added this because for some reason the whole data isn't getting downloaded in the csv
            const blob = new Blob([data], { type: '.csv' });
            const url = URL.createObjectURL(blob);

            // Create a link to download it
            const dlEle = document.createElement('a');
            dlEle.href = url;
            dlEle.setAttribute('download', `${name}.csv`);
            dlEle.click();
        }
    }

    exportZIP(data, name) {
        if (data) {
            const dlEle = document.createElement('a');
            const blob = new Blob([data], { type: 'application/zip' });
            const url = URL.createObjectURL(blob);
            dlEle.setAttribute('download', `${name}.zip`);
            dlEle.href = url;
            dlEle.click();
        }
    }

    async canDeactivate() {
        if (this.raffleService.hasChanges()) {
            return await this.promptForSave();
        }

        return true;
    }

    async save() {
        // each form might have special logic/validation so first we need to await the result of preSave
        const canSave = await this.preSave();
        if (canSave === false) {
            return;
        }

        try {
            this.busy = true;

            // only run the actual save if there are changes
            if (this.raffleService.hasChanges()) {
                const newRaffle = (this.raffle.raffleId == null);
                await this.raffleService.save(this.raffle);

                if (newRaffle) {
                    ServiceHelper.analyticsSvc.logCreateRaffle(this.raffle.accessCode);
                }
            }

            // Call post save as each form may act different
            await this.postSave();

            return true;

        } catch (error) {
            console.error('Error saving raffle', error);
            ServiceHelper.displayAlert('Error Saving Raffle', error);
            return false;
        } finally {
            this.busy = false;
        }
    }

    async promptForSave(): Promise<boolean> {
        const resp = await ServiceHelper.getChoiceSelection('Save Changes?', 'Would you like to save your changes?');
        if (resp === ChoiceSelectValue.Yes) {
            return await this.save();
        } else if (resp === ChoiceSelectValue.No) {
            this.raffleService.revert();
            return true;
        }

        return false;
    }

    async revertChanges() {
        const resp = await this.confirmSomething('Are you sure?', 'This will revert any changes you have made.');
        if (resp === true) {
            this.raffleService.revert();
        }
    }


    commitChanges() {
        // basically just set the current raffle to a new copy of itself
        // this will trigger a re-bind of the server raffle
        this.raffleService.currentRaffle = new Raffle(this.raffle);
    }

    protected async loadData() {
        // if the user is not a super admin, then they cannot edit the raffle if it is not draft
        this.readonly = this.raffle.status !== RaffleStatus.Draft && !this.authService.isSuper;
        localStorage.setItem('arlocked', this.isLocked ? '1' : '0'); // set the local storage value
        await this.postLoad();
        this.loaded = true;
    }


    protected onRaffleEvent?(event: IRaffleEvent): void;
    protected async postLoad(): Promise<boolean> {
        this.ticketValid = await this.areTicketsValid();
        return true;
    }
    protected async preSave(): Promise<boolean> { return true; }
    protected async postSave() { }

    private checkValidity(fields: any[]) {
        let missing = 0;
        for (const field of fields) {
            if (!field) { missing += 1; }
        }
        if (missing > 0) { return false; }
        return missing === 0;
    }

}
